import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Device } from '@/api/devices/types';

export interface EnumMappings {
    status: Record<string, string>;
    mode: Record<string, string>;
    fanSpeed: Record<string, string>;
    operationMode: Record<string, string>;
    louverPosition: Record<string, string>;
    [key: string]: Record<string, string>; // Add index signature for string keys
}

interface EnumMappingsContextType {
    enumMappings: EnumMappings;
    updateEnumMappings: (device: Device) => void;
}

const defaultEnumMappings: EnumMappings = {
    status: {},
    mode: {},
    fanSpeed: {},
    operationMode: {},
    louverPosition: {},
};

const EnumMappingsContext = createContext<EnumMappingsContextType>({
    enumMappings: defaultEnumMappings,
    updateEnumMappings: () => { },
});

export const useEnumMappings = () => useContext(EnumMappingsContext);

interface EnumMappingsProviderProps {
    children: ReactNode;
}

export function EnumMappingsProvider({ children }: EnumMappingsProviderProps) {
    const [enumMappings, setEnumMappings] = useState<EnumMappings>(defaultEnumMappings);

    // Memoize updateEnumMappings to prevent it from being recreated on every render
    const updateEnumMappings = useCallback((device: Device) => {
        setEnumMappings(prevMappings => {
            const newMappings = { ...prevMappings };

            // Extract enum mappings from the device data and merge with existing mappings
            if (device.latest_data.status_read?.enum) {
                console.log(`Updating status enum mappings for device ${device.device_id}:`, device.latest_data.status_read.enum);
                newMappings.status = { ...newMappings.status, ...device.latest_data.status_read.enum };
            }

            if (device.latest_data.mode_read?.enum) {
                console.log(`Updating mode enum mappings for device ${device.device_id}:`, device.latest_data.mode_read.enum);
                newMappings.mode = { ...newMappings.mode, ...device.latest_data.mode_read.enum };
            }

            if (device.latest_data.fan_mode_read?.enum) {
                console.log(`Updating fan mode enum mappings for device ${device.device_id}:`, device.latest_data.fan_mode_read.enum);
                newMappings.fanSpeed = { ...newMappings.fanSpeed, ...device.latest_data.fan_mode_read.enum };
            }

            if (device.latest_data.operation_mode_read?.enum) {
                console.log(`Updating operation mode enum mappings for device ${device.device_id}:`, device.latest_data.operation_mode_read.enum);
                newMappings.operationMode = { ...newMappings.operationMode, ...device.latest_data.operation_mode_read.enum };
            }

            if (device.latest_data.louver_position_read?.enum) {
                console.log(`Updating louver position enum mappings for device ${device.device_id}:`, device.latest_data.louver_position_read.enum);
                newMappings.louverPosition = { ...newMappings.louverPosition, ...device.latest_data.louver_position_read.enum };
            }

            // Log the updated mappings for debugging
            console.log('Updated enum mappings:', {
                status: Object.keys(newMappings.status).length,
                mode: Object.keys(newMappings.mode).length,
                fanSpeed: Object.keys(newMappings.fanSpeed).length,
                operationMode: Object.keys(newMappings.operationMode).length,
                louverPosition: Object.keys(newMappings.louverPosition).length,
            });

            return newMappings;
        });
    }, []);

    return (
        <EnumMappingsContext.Provider value={{ enumMappings, updateEnumMappings }}>
            {children}
        </EnumMappingsContext.Provider>
    );
}